<template>
	<div class="card-viewer" :style="{ '--card-color': cardsStore.availableColors[color] }">
		<div class="card-container" @pointerdown="startInteraction" @pointerup="endInteraction">
			<div class="card" :class="{ 'is-flipped': isFlipped && !frozen }">
				<div class="card-face card-front" :class="{frozen}">
                    <div class="card-top">
                        <div class="card-name">{{ label }}</div>
                        <div class="qromo-logo"><img src="https://img.qromo.io/img/logo/logo-white-vivo.svg" alt="logo qromo"></div>
                    </div>
					<div class="card-bottom">
						<div class="card-last-four">•••• {{ lastFourDigits }}</div>
						<div class="card-logo">
							<img :src="'https://img.qromo.io/img/icons/'+(['visa', 'mastercard'].includes(network) ? network : 'generic')+'-cards.svg'" :alt="network+' logo'" />
						</div>
					</div>
					<div class="frozen-image" v-if="frozen"><img src="https://img.qromo.io/img/freeze-card.png"></div>
				</div>
				<div class="card-face card-back">
					<div class="magnetic-strip"></div>
                    <div class="card-details">
                        <div class="card-detail">
                            <div class="card-detail-label">Card number</div>
                            <div class="card-detail-value">
                                <div class="card-info card-info-number" v-show="isShowingDetails" :id="'card-digits-'+cardId+'-'+randomId"></div>
                                <div class="card-info card-info-number" v-show="!isShowingDetails">{{ cardDigits }}</div>
								<div class="copy-icon" :class="{invisible: !isShowingDetails}">
									<div class="copy-icon-icon" v-html="getIcon('iconCopy')"></div>
									<div class="copy-icon-frame" :id="'card-digits-copy-'+cardId+'-'+randomId"></div>
								</div>
                            </div>
                        </div>
                        
                        <div class="card-detail-row">
                            <div class="card-detail">
                                <div class="card-detail-label">Data di scadenza</div>
                                <div class="card-detail-value">
                                    <div class="card-info card-info-date" v-show="isShowingDetails" :id="'card-date-'+cardId+'-'+randomId"></div>
                                    <div class="card-info card-info-date" v-show="!isShowingDetails">••/••</div>
									<div class="copy-icon" :class="{invisible: !isShowingDetails}">
										<div class="copy-icon-icon" v-html="getIcon('iconCopy')"></div>
										<div class="copy-icon-frame" :id="'card-date-copy-'+cardId+'-'+randomId"></div>
									</div>
                                </div>
                            </div>
                            
                            <div class="card-detail">
                                <div class="card-detail-label">CVV</div>
                                <div class="card-detail-value">
                                    <div class="card-info card-info-cvv" v-show="isShowingDetails" :id="'card-cvv-'+cardId+'-'+randomId"></div>
                                    <div class="card-info card-info-cvv" v-show="!isShowingDetails">•••</div>
									<div class="copy-icon" :class="{invisible: !isShowingDetails}">
										<div class="copy-icon-icon" v-html="getIcon('iconCopy')"></div>
										<div class="copy-icon-frame" :id="'card-cvv-copy-'+cardId+'-'+randomId"></div>
									</div>
                                </div>
                            </div>
                        </div>
                    </div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {useNotificationStore, useSvgStore} from "@/shared/stores";
import {useCardsStore} from "@/console-new/stores/cards.store";

export default {
    setup() {
        const svgStore = useSvgStore();
        const cardsStore = useCardsStore();
		const notificationStore = useNotificationStore();
        return {
            getIcon: svgStore.get,
            cardsStore,
			notificationStore,
        }
    },
	name: "CardViewer",
	props: {
		cardId: {
			type: Number,
			default: -1
		},
		lastFourDigits: {
			type: String,
			default: '0000'
		},
		network: {
			type: String,
			default: 'mastercard'
		},
		label: {
			type: String,
			required: true
		},
        color: {
            type: String,
            default: 'blue'
        },
		frozen: {
			type: Boolean,
			default: false
		}
	},
    data() {
        return {
            isFlipped: false,
			numberElement: null,
			numberCopyElement: null,
			expiryElement: null,
			expiryCopyElement: null,
			cvcElement: null,
			cvcCopyElement: null,
			randomId: Math.random().toString(36).substring(2, 15),
			startX: 0,
			startY: 0,
			moveThreshold: 15, // pixels
			interactionStarted: false
        }
    },
    methods: {
		startInteraction(event) {
			this.interactionStarted = true;
			this.startX = event.clientX;
			this.startY = event.clientY;
		},
		
		endInteraction(event) {
			if (!this.interactionStarted || this.frozen) {
				return;
			}
			
			// Calculate movement distance
			const deltaX = Math.abs(event.clientX - this.startX);
			const deltaY = Math.abs(event.clientY - this.startY);
			
			// Only flip if movement was minimal (user intended to click, not drag)
			if (deltaX < this.moveThreshold && deltaY < this.moveThreshold) {
				this.isFlipped = !this.isFlipped;
			}
			
			// Reset interaction flag
			this.interactionStarted = false;
		},
		
        toggleFlip() {
			// Keep this method for compatibility with other code that might call it
			if(!this.frozen) {
				this.isFlipped = !this.isFlipped;
			}
        }
    },
	computed: {
		cardDigits() {
			return "•••• •••• •••• " + this.lastFourDigits;
		},
		isShowingDetails() {
			return this.cardsStore.stripe.detailsCardId === this.cardId;
		}
	},
	watch: {
		'isShowingDetails': function(newVal) {
			if (newVal) {
				const card = this.cardsStore.getCardById(this.cardId);
				if (!card || !this.cardsStore.stripe.elements) {
					console.error("Card or Stripe elements not available for card ID:", this.cardId);
					return;
				}
				if(!this.isFlipped) {
					this.toggleFlip();
				}

				// Number
				this.numberElement = this.cardsStore.stripe.elements.create('issuingCardNumberDisplay', {
					issuingCard: card.stripe_id,
					nonce: this.cardsStore.stripe.nonce,
					ephemeralKeySecret: this.cardsStore.stripe.ephemeralKey,
					style: {
						base: {
							color: '#fff',
							fontSize: '16px',
							fontWeight: '500',
							fontFamily: 'Montserrat, sans-serif',
						},
					},
				});
				this.numberElement.mount('#card-digits-' + this.cardId + '-' + this.randomId);

				this.numberCopyElement = this.cardsStore.stripe.elements.create('issuingCardCopyButton', {
					toCopy: 'number',
					style: {
						base: {
							fontSize: '12px',
							lineHeight: '24px',
						},
					},
				});
				this.numberCopyElement.on('click', () => {
					this.notificationStore.showNotification('info-copied');
				});
				this.numberCopyElement.mount('#card-digits-copy-' + this.cardId + '-' + this.randomId);

				// Expiry
				this.expiryElement = this.cardsStore.stripe.elements.create('issuingCardExpiryDisplay', {
					issuingCard: card.stripe_id,
					nonce: this.cardsStore.stripe.nonce,
					ephemeralKeySecret: this.cardsStore.stripe.ephemeralKey,
					style: {
						base: {
							color: '#fff',
							fontSize: '16px',
							fontWeight: '500',
							fontFamily: 'Montserrat, sans-serif',
						},
					},
				});
				this.expiryElement.mount('#card-date-' + this.cardId + '-' + this.randomId);

				this.expiryCopyElement = this.cardsStore.stripe.elements.create('issuingCardCopyButton', {
					toCopy: 'expiry',
					style: {
						base: {
							fontSize: '12px',
							lineHeight: '24px',
						},
					},
				});
				this.expiryCopyElement.on('click', () => {
					this.notificationStore.showNotification('info-copied');
				});
				this.expiryCopyElement.mount('#card-date-copy-' + this.cardId + '-' + this.randomId);

				// CVC
				this.cvcElement = this.cardsStore.stripe.elements.create('issuingCardCvcDisplay', {
					issuingCard: card.stripe_id,
					nonce: this.cardsStore.stripe.nonce,
					ephemeralKeySecret: this.cardsStore.stripe.ephemeralKey,
					style: {
						base: {
							color: '#fff',
							fontSize: '16px',
							fontWeight: '500',
							fontFamily: 'Montserrat, sans-serif',
						},
					},
				});
				this.cvcElement.mount('#card-cvv-' + this.cardId + '-' + this.randomId);

				this.cvcCopyElement = this.cardsStore.stripe.elements.create('issuingCardCopyButton', {
					toCopy: 'cvc',
					style: {
						base: {
							fontSize: '12px',
							lineHeight: '24px',
						},
					},
				});
				this.cvcCopyElement.on('click', () => {
					this.notificationStore.showNotification('info-copied');
				});
				this.cvcCopyElement.mount('#card-cvv-copy-' + this.cardId + '-' + this.randomId);


			} else {
				if (this.numberElement) {
					this.numberElement.unmount();
					this.numberElement = null;
				}
				if (this.numberCopyElement) {
					this.numberCopyElement.unmount();
					this.numberCopyElement = null;
				}
				if (this.expiryElement) {
					this.expiryElement.unmount();
					this.expiryElement = null;
				}
				if (this.expiryCopyElement) {
					this.expiryCopyElement.unmount();
					this.expiryCopyElement = null;
				}
				if (this.cvcElement) {
					this.cvcElement.unmount();
					this.cvcElement = null;
				}
				if (this.cvcCopyElement) {
					this.cvcCopyElement.unmount();
					this.cvcCopyElement = null;
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">
.card-viewer {
    display: flex;
    justify-content: center;
    align-items: center;
    --card-color: #0752A3;
    width: 100%;
}

.card-container {
	perspective: 1000px;
	width: 320px;
	height: 200px;
	cursor: pointer;
	user-select: none;
	-webkit-user-select: none;
	-webkit-tap-highlight-color: transparent;
	touch-action: manipulation;

    @media (max-width: 380px) {
        width: 100%;
    }
}

.card {
	width: 100%;
	height: 100%;
	position: relative;
	transition: transform 0.6s;
	transform-style: preserve-3d;
	
	&.is-flipped {
		transform: rotateY(180deg);
	}
}

.card-face {
	position: absolute;
	width: 100%;
	height: 100%;
	backface-visibility: hidden;
	border-radius: 13px;
	box-shadow: 4px 4px 26px 0 rgba(0, 0, 0, 0.40), 1px 1px 2px 0 rgba(255, 255, 255, 0.50) inset, -1px -1px 2px 0 rgba(0, 0, 0, 0.25) inset;
}

.card-front {
	background-color: var(--card-color);
	padding: 14px 16px;
	color: white;
	font-family: 'Inter', sans-serif;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	
	&.frozen {
		pointer-events: none;
	}

	.frozen-image {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1;
		border-radius: 13px;
		background-color: rgba(0, 230, 255, 0.55);

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			border-radius: 13px;
		}
	}

    .card-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        .card-name {
            font-size: 18px;
            max-width: 220px;
            @include truncateString(1);
        }

        .qromo-logo {
            width: 22px;
            img {
                width: 100%;
                height: 100%;
            }
        }
    }

	.card-bottom {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;

		.card-last-four {
			font-size: 20px;
			font-weight: 500;
		}

		.card-logo {
			display: flex;
			justify-content: flex-end;
			align-items: flex-end;
			width: 60px;
			img {
				width: 100%;
				height: 100%;
			}
		}
	}
}

.card-back {
	background-color: var(--card-color);
	transform: rotateY(180deg);
	display: flex;
	flex-direction: column;
    justify-content: space-between;
    box-shadow: none;
	
	.magnetic-strip {
		background-color: #2D2D2D;
		height: 40px;
		width: 100%;
        margin: 20px 0;
	}

    .card-details {
        padding: 0 20px;
        color: white;
        font-family: 'Inter', sans-serif;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 20px;
    }
    
    .card-detail-row {
        display: flex;
        justify-content: space-between;
        
        .card-detail {
            width: 45%;
        }
    }
    
    .card-detail-label {
        font-size: 12px;
		text-align: left;
    }
    
    .card-detail-value {
        font-size: 14px;
        font-weight: 500;
		text-align: left;
        display: flex;
        align-items: center;

        .card-info {
            font-size: 16px;
            font-weight: 500;

			&-number {
				min-width: 175px;
			}

			&-date {
				min-width: 50px;
			}

			&-cvv {
				min-width: 30px;
			}
        }

        .copy-icon {
            width: 36px;
            height: 32px;
            padding: 9px 12px;
            cursor: pointer;
			opacity: 1;
			transition: opacity 0.3s ease-in-out;
			position: relative;
			display: flex;
			justify-content: center;
			align-items: center;

			&-icon {
				width: 100%;
				height: 100%;
				display: flex;
				justify-content: center;
				align-items: center;
			}

			&-frame {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
			}

            &:deep(svg) {
                width: 100%;
                height: 100%;

                .fill {
                    fill: white;
                }

                .stroke {
                    stroke: white;
                }
            }

			&.invisible {
				opacity: 0;
			}

        }
    }
}
</style>